import React from 'react';
import { Link } from 'react-router-dom';
import { ShareIcon } from '@heroicons/react/24/outline';
import {
    TOOLS, STORY_QUOTES, STORY_STEPS, STORY_PLAN, STORY_BUILD, STORY_COUNSELOR, STORY_COST, STORY_TOUR,
} from '../../../content/landingHome';
import { TREND } from '../../../data/landingTrends';
import { PlanningData } from './PlanningSection';
import HeroTestimonials from './Testimonials';
import { Eyebrow, StartFree, Arrow, Source } from './parts';

// The landing page's story order (#5128): the bands founder pick A of the
// redesign mocks (#5093) adds, built from the mock round's components
// (branch mocks/5093, never merged). Every word is content/landingHome.js's;
// every figure a catalog constant, a sourced trend or the visitor's price
// from useSeasonPassPrice, never typed here. Each band carries its
// data-landing-section id and each link its data-cta id (content/
// landingIds.js), so the Desk's landing watch reads the new order on day one.
//
// Accessibility (design.md section 6): every band is an h2 under the page's
// one h1, its items h3; the steps are an <ol> (a sequence), the parts and
// tiles <ul role="list">; every link at least 44px tall.

const H2 = 'font-serif text-3xl font-bold leading-tight text-[#1A4D2E] [text-wrap:balance] sm:text-4xl';
const LEAD = 'mx-auto mt-3 max-w-2xl text-lg leading-relaxed text-[#4A4A4A] [text-wrap:pretty]';
const TEXT_LINK = 'inline-flex min-h-[44px] items-center gap-1 font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4';

function Head({ eyebrow, title, sub }) {
    return (
        <div className="mx-auto mb-7 max-w-3xl text-center">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h2 className={H2}>{title}</h2>
            {sub && <p className={LEAD}>{sub}</p>}
        </div>
    );
}

/** The real quotes in their own band (question 8). No quotes, no band:
 * absence, never a placeholder, and its Start free goes with it. */
export function QuotesBand({ testimonials, onStart }) {
    if (!(Array.isArray(testimonials) && testimonials.some((t) => t?.quote))) return null;
    return (
        <section data-landing-section="quotes" data-testid="landing-story-quotes" className="bg-[#FDFCF7] px-4 pb-10 pt-6 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
                {/* The carousel carries the band's heading for screen readers. */}
                <div aria-hidden="true"><Eyebrow>{STORY_QUOTES.eyebrow}</Eyebrow></div>
                <HeroTestimonials testimonials={testimonials} />
                <div className="mt-5 flex justify-center"><StartFree onStart={onStart} cta="quotes_start_free" size="md" /></div>
            </div>
        </section>
    );
}

/** One story in three steps. Keeps the #how anchor How it works had. */
export function StorySteps({ onStart }) {
    return (
        <section id="how" data-landing-section="story" data-testid="landing-story-steps" className="scroll-mt-20 bg-white px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
            <div className="mx-auto max-w-6xl">
                <Head eyebrow={STORY_STEPS.eyebrow} title={STORY_STEPS.title} />
                <ol className="m-0 grid list-none gap-4 p-0 lg:grid-cols-3">
                    {STORY_STEPS.steps.map((s, i) => (
                        <li key={s.title} className="flex min-w-0 gap-4 rounded-2xl border border-[#E0DED8] bg-[#FDFCF7] p-5">
                            <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1A4D2E] font-serif text-lg font-bold text-white">{i + 1}</span>
                            <div className="min-w-0">
                                <h3 className="font-serif text-xl font-bold text-[#1A4D2E]">{s.title}</h3>
                                <p className="mt-1 text-base leading-snug text-[#4A4A4A] [text-wrap:pretty]">{s.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="mt-7 flex justify-center"><StartFree onStart={onStart} cta="story_start_free" size="md" /></div>
            </div>
        </section>
    );
}

/** Everything the plan covers: the data band, then six parts, each a guide. */
export function PlanGrid() {
    return (
        <section id="plan" data-landing-section="plan" data-testid="landing-story-plan" className="scroll-mt-20 bg-[#FDFCF7] py-10 lg:py-16">
            <div className="px-4 sm:px-6 lg:px-8"><Head eyebrow={STORY_PLAN.eyebrow} title={STORY_PLAN.title} sub={STORY_PLAN.sub} /></div>
            <PlanningData />
            <div className="px-4 sm:px-6 lg:px-8">
                <ul role="list" className="m-0 mx-auto grid max-w-6xl list-none gap-x-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
                    {STORY_PLAN.items.map((p) => (
                        <li key={p.title} className="min-w-0 border-t border-[#E0DED8] py-3">
                            <Link to={p.to} data-cta="plan_guide" className="group block min-h-[44px]">
                                <h3 className="font-serif text-xl font-bold text-[#1A4D2E] group-hover:underline">{p.title}</h3>
                                <span className="mt-0.5 block text-base leading-snug text-[#4A4A4A] [text-wrap:pretty]">{p.body}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

/** The four builder tools as one band, one sourced trend line, the guide.
 * Keeps the #build anchor. */
export function BuildBand() {
    const trend = TREND[STORY_BUILD.trend];
    return (
        <section id="build" data-landing-section="build" data-testid="landing-story-build" className="scroll-mt-20 bg-white px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
            <div className="mx-auto max-w-6xl">
                <Head eyebrow={STORY_BUILD.eyebrow} title={STORY_BUILD.title} sub={STORY_BUILD.sub} />
                <ul role="list" className="m-0 grid list-none grid-cols-2 gap-2.5 p-0 lg:grid-cols-4">
                    {TOOLS.map((t) => (
                        <li key={t.key} className="min-w-0" data-build-tool={t.key}>
                            <Link to={t.guide || STORY_BUILD.guide} data-cta={t.tileCta}
                                className="flex h-full min-h-[44px] flex-col rounded-2xl border border-[#E0DED8] bg-[#FDFCF7] p-3.5 hover:border-[#1A4D2E]/40">
                                <h3 className="font-serif text-lg font-bold leading-tight text-[#1A4D2E]">{t.name}</h3>
                                <span className="mt-1 text-sm leading-snug text-[#4A4A4A]">{t.short}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
                {trend && (
                    <p data-testid="landing-story-trend" className="mx-auto mt-6 max-w-2xl text-center text-base leading-snug text-[#2C2C2C]">
                        <strong className="text-[#1A4D2E]">{trend.lead}</strong> {trend.line}
                        <Source>{trend.source}</Source>
                    </p>
                )}
                <p className="mt-5 text-center">
                    <Link to={STORY_BUILD.guide} data-cta="build_guide" className={TEXT_LINK}>
                        {STORY_BUILD.guideLink} <Arrow />
                    </Link>
                </p>
            </div>
        </section>
    );
}

/** Stratia beside a counselor, in one card. Its master's line shows only
 * beside the Master's abroad section, as the full section's does. */
export function CounselorCard({ masters = false }) {
    const c = STORY_COUNSELOR;
    return (
        <section id="with-counselor" data-landing-section="with_counselor" data-testid="landing-story-counselor" className="scroll-mt-20 bg-[#FDFCF7] px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
            <div className="mx-auto flex max-w-3xl items-start gap-4 rounded-2xl border border-[#E0DED8] bg-white p-5 sm:gap-5 sm:p-7">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F0F7F1]">
                    <ShareIcon className="h-6 w-6 text-[#1A4D2E]" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                    <Eyebrow>{c.eyebrow}</Eyebrow>
                    <h2 className="font-serif text-2xl font-bold leading-tight text-[#1A4D2E] [text-wrap:balance] sm:text-3xl">{c.title}</h2>
                    <p className="mt-2 text-base leading-relaxed text-[#2C2C2C]">{c.body}</p>
                    <p className="mt-2 text-base font-semibold text-[#1A4D2E]">{c.partner}</p>
                    {masters && <p data-testid="landing-story-counselor-masters" className="mt-2 text-base text-[#4A4A4A]">{c.masters}</p>}
                    <p className="mt-2 flex flex-wrap gap-x-5">
                        <Link to={c.shareGuide} data-cta="with_counselor_sharing_guide" className={TEXT_LINK}>{c.shareLink}<Arrow /></Link>
                        <Link to={c.guide} data-cta="with_counselor_guide" className={TEXT_LINK}>{c.guideLink}</Link>
                    </p>
                </div>
            </div>
        </section>
    );
}

/** What it costs. `pass` and `graduation` are the visitor's prices
 * ({label}), read by the page through useSeasonPassPrice and the region;
 * `graduation` is null until one_payment_passes is on for everyone.
 * `oneFreeRead` (#5909): one_free_read is on for everyone, so a new account
 * gets its first full read free and no trial; that card takes the trial's. */
export function CostBand({ onStart, pass, graduation = null, oneFreeRead = false }) {
    const k = STORY_COST;
    const second = oneFreeRead ? { key: 'first_read', ...k.firstRead } : { key: 'trial', ...k.trial };
    const items = [
        { key: 'free', title: k.free.title, body: k.free.body },
        second,
        { key: 'pass', title: k.pass.title(pass.label), body: k.pass.body },
        ...(graduation ? [{ key: 'graduation', title: k.graduation.title(graduation.label), body: k.graduation.body }] : []),
    ];
    return (
        <section id="cost" data-landing-section="cost" data-testid="landing-story-cost" className="scroll-mt-20 bg-[#1A4D2E] px-4 py-10 text-white sm:px-6 lg:px-8 lg:py-14">
            <div className="mx-auto max-w-4xl text-center">
                <Eyebrow dark>{k.eyebrow}</Eyebrow>
                <h2 className="font-serif text-3xl font-bold leading-tight [text-wrap:balance] sm:text-4xl">{k.title}</h2>
                <ul role="list" className={`m-0 mx-auto mt-5 grid list-none gap-3 p-0 text-left ${items.length > 3 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'max-w-2xl sm:grid-cols-3'}`}>
                    {items.map((it) => (
                        <li key={it.key} data-testid={`landing-story-cost-${it.key}`} className="min-w-0 rounded-xl bg-white/10 p-4">
                            <h3 className="font-semibold">{it.title}</h3>
                            <p className="mt-1 text-sm leading-snug text-[#E3F0E2]">{it.body}</p>
                        </li>
                    ))}
                </ul>
                <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-6">
                    <StartFree onStart={onStart} cta="cost_start_free" tone="white" size="md" />
                    <Link to="/pricing" data-cta="cost_pricing" className="inline-flex min-h-[44px] items-center gap-1 font-semibold text-white underline decoration-white/40 underline-offset-4">{k.pricing} <Arrow /></Link>
                </div>
            </div>
        </section>
    );
}

/** The six product screens, folded to one line to /tour (question 4). */
export function TourLine() {
    return (
        <div data-landing-section="tour_line" data-testid="landing-story-tour" className="bg-[#FDFCF7] px-4 pt-10 sm:px-6 lg:px-8">
            <p className="mx-auto max-w-3xl text-center text-base text-[#4A4A4A]">
                {STORY_TOUR.lead}{' '}
                <Link to={STORY_TOUR.to} data-cta="tour_link" className="inline-flex min-h-[44px] items-center font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4">{STORY_TOUR.link}</Link>
            </p>
        </div>
    );
}
